import css from './wordQueue.module.scss';

import { useRef, useState, useCallback } from 'react';

type TRefState = {
  card1: HTMLElement | null;
  card2: HTMLElement | null;
  isAnimating: boolean;
};

const { SHIFT } = css;

const ANIMATION = {
  keyframes: [
    { transform: 'translateX(0)' },
    { transform: `translateX(-${SHIFT})` },
  ],
  options: {
    duration: 400,
    easing: 'ease-in-out',
    fill: 'forwards', // do not reset the animation on its finish
  } satisfies KeyframeAnimationOptions,
};

export default function useShiftAnimation() {
  const [card1, setCard1] = useState<HTMLDivElement | null>(null);
  const [card2, setCard2] = useState<HTMLDivElement | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const { current: st } = useRef<TRefState>({
    card1: null,
    card2: null,
    isAnimating: false,
  });
  Object.assign(st, { card1, card2 });

  const doShift = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        const { card1, card2, isAnimating } = st;
        if (isAnimating || !card1 || !card2) {
          resolve();
          return;
        }

        st.isAnimating = true;
        setIsAnimating(true);
        document.body.setAttribute('data-shift-animation', 'true');

        card1.animate(ANIMATION.keyframes, ANIMATION.options);
        const animation = card2.animate(ANIMATION.keyframes, ANIMATION.options);
        animation.onfinish = () => {
          st.isAnimating = false;
          setIsAnimating(false);
          resolve();
          setTimeout(() => {
            // let some time to the app to rerender both cards.
            document.body.removeAttribute('data-shift-animation');
          }, 20);
        };
        animation.oncancel = reject;
      }),
    []
  );

  return { setCard1, setCard2, doShift, isAnimating };
}
